import type {ReactNode} from 'react';
import React from 'react';

import AnnotatableContent from '../AnnotatableContent/AnnotatableContent';
import AnnotationSupplementNoteIndicator from './AnnotationSupplementNoteIndicator';
import {useSupplementPresence} from '../store/AnnotationModeStore';
import {buildSupplementRegionId} from '../shared/annotationRegions';
import {useCanViewAnnotations} from '../mode/useCanViewAnnotations';
import {useSupplementCard} from './useSupplementCard';

interface AnnotatablePassageSupplementProps {
  supplementId: string;
  /** Language of the rendered passage ('es' when translated); null = original. */
  lang?: string | null;
  /** The passage's display name, listing it on the question for the notes panel. */
  name?: string;
  children: ReactNode;
}

/**
 * A passage takes precise text marks (#18667: whole-passage notes weren't useful
 * enough) in its own region, so they carry across the questions sharing it and its
 * collapse toggle stops resizing the prompt's offsets under every later mark.
 */
export default function AnnotatablePassageSupplement({
  supplementId,
  lang = null,
  name = '',
  children
}: AnnotatablePassageSupplementProps) {
  const canViewAnnotations = useCanViewAnnotations();

  if (!canViewAnnotations) {
    return <>{children}</>;
  }

  return (
    <PassageRegion supplementId={supplementId} lang={lang} name={name}>
      {children}
    </PassageRegion>
  );
}

function PassageRegion({
  supplementId,
  lang,
  name,
  children
}: {
  supplementId: string;
  lang: string | null;
  name: string;
  children: ReactNode;
}) {
  const regionId = buildSupplementRegionId(supplementId);
  const {regionRef, region, cardClassName, notedMarks, indicatorColor} = useSupplementCard(
    regionId,
    lang
  );
  useSupplementPresence(regionId, name, 'passage');

  return (
    <>
      <div ref={regionRef} data-testid='annotatable-passage-supplement' className={cardClassName}>
        {/* `contents`: the card stays the passage's only added box. */}
        <AnnotatableContent
          regionId={regionId}
          lang={lang}
          className='contents'
          showNoteIndicators={false}
        >
          {children}
        </AnnotatableContent>
      </div>
      <AnnotationSupplementNoteIndicator
        region={region}
        notedMarks={notedMarks}
        color={indicatorColor}
      />
    </>
  );
}
